import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
} from 'react-native';
import { Icon } from './Icon';
import { spacing, fontWeights, borderRadius } from '../theme';

export interface NumpadInputModalProps {
  visible: boolean;
  title: string;
  unit?: string;
  rangeText?: string;
  initialValue: string | number;
  formatType?: 'number' | 'integer';
  maxLength?: number;
  onConfirm: (value: string) => void;
  onClose: () => void;
}

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const NumpadInputModal: React.FC<NumpadInputModalProps> = ({
  visible,
  title,
  unit,
  rangeText,
  initialValue,
  formatType = 'number',
  maxLength = 12,
  onConfirm,
  onClose,
}) => {
  const [rawDigits, setRawDigits] = useState<string>('');

  useEffect(() => {
    if (!visible) return;
    const strVal = String(initialValue || '').replace(/\D/g, '');
    setRawDigits(strVal);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        setRawDigits((prev) => {
          if (prev.length < maxLength) {
            return prev === '0' ? e.key : prev + e.key;
          }
          return prev;
        });
      } else if (e.key === 'Backspace') {
        setRawDigits((prev) => prev.slice(0, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setRawDigits((currentDigits) => {
          const numericVal = parseInt(currentDigits, 10);
          if (isNaN(numericVal) || numericVal <= 0) {
            onConfirm(formatType === 'number' ? '0' : '0');
          } else if (formatType === 'number') {
            onConfirm(formatNumber(numericVal));
          } else {
            onConfirm(String(numericVal));
          }
          onClose();
          return currentDigits;
        });
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [visible, initialValue, maxLength, formatType, onConfirm, onClose]);

  const handleNumpadPress = (key: string) => {
    if (key === 'C') {
      setRawDigits('');
    } else if (key === 'DEL') {
      setRawDigits((prev) => prev.slice(0, -1));
    } else if (key === '000') {
      if (rawDigits && rawDigits !== '0' && rawDigits.length + 3 <= maxLength) {
        setRawDigits((prev) => prev + '000');
      }
    } else if (key === '000000') {
      if (rawDigits && rawDigits !== '0' && rawDigits.length + 6 <= maxLength) {
        setRawDigits((prev) => prev + '000000');
      }
    } else {
      if (rawDigits.length < maxLength) {
        setRawDigits((prev) => (prev === '0' ? key : prev + key));
      }
    }
  };

  const handleConfirm = () => {
    const numericVal = parseInt(rawDigits, 10);
    if (isNaN(numericVal) || numericVal <= 0) {
      onConfirm(formatType === 'number' ? '0' : '0');
    } else if (formatType === 'number') {
      onConfirm(formatNumber(numericVal));
    } else {
      onConfirm(String(numericVal));
    }
    onClose();
  };

  const getDisplayedText = (): string => {
    if (!rawDigits) return '0';
    const num = parseInt(rawDigits, 10);
    if (isNaN(num)) return '0';
    return formatType === 'number' ? formatNumber(num) : String(num);
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <TouchableOpacity
          style={styles.numpadModalCard}
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          <Text style={styles.modalTitle}>{title}</Text>
          {unit ? <Text style={styles.modalSubTitle}>{unit}</Text> : <View style={{ height: 12 }} />}

          <View style={styles.modalDisplayBox}>
            <TextInput
              style={styles.modalDisplayText}
              value={getDisplayedText()}
              onChangeText={(text) => {
                const numericStr = text.replace(/\D/g, '');
                if (numericStr.length <= maxLength) {
                  setRawDigits(numericStr);
                }
              }}
              keyboardType="numeric"
              returnKeyType="done"
              onSubmitEditing={handleConfirm}
              autoFocus={true}
              selectTextOnFocus={true}
            />
          </View>

          {rangeText ? (
            <Text style={styles.modalRangeText}>{rangeText}</Text>
          ) : (
            <View style={{ height: 12 }} />
          )}

          {/* Keypad Grid 4x3 */}
          <View style={styles.keypadGrid}>
            <View style={styles.keypadRow}>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('1')}>
                <Text style={styles.keypadNumText}>1</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('2')}>
                <Text style={styles.keypadNumText}>2</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('3')}>
                <Text style={styles.keypadNumText}>3</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.keypadRow}>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('4')}>
                <Text style={styles.keypadNumText}>4</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('5')}>
                <Text style={styles.keypadNumText}>5</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('6')}>
                <Text style={styles.keypadNumText}>6</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.keypadRow}>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('7')}>
                <Text style={styles.keypadNumText}>7</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('8')}>
                <Text style={styles.keypadNumText}>8</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('9')}>
                <Text style={styles.keypadNumText}>9</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.keypadRow}>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('C')}>
                <Text style={styles.keypadActionText}>C</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('0')}>
                <Text style={styles.keypadNumText}>0</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.keypadBtn} onPress={() => handleNumpadPress('DEL')}>
                <Icon name="backspace" size={24} color="#334155" />
              </TouchableOpacity>
            </View>
          </View>

          {formatType === 'number' && (
            <View style={styles.quickZeroRow}>
              <TouchableOpacity
                style={styles.quickZeroBtn}
                onPress={() => handleNumpadPress('000')}
              >
                <Text style={styles.quickZeroBtnText}>+000 (Nghìn)</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.quickZeroBtn}
                onPress={() => handleNumpadPress('000000')}
              >
                <Text style={styles.quickZeroBtnText}>+000.000 (Triệu)</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Action Buttons Footer */}
          <View style={styles.modalActionRow}>
            <TouchableOpacity style={styles.modalConfirmBtn} onPress={handleConfirm}>
              <Text style={styles.modalConfirmBtnText}>Xác nhận</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.modalCancelBtn} onPress={onClose}>
              <Text style={styles.modalCancelBtnText}>Hủy bỏ</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
  },
  numpadModalCard: {
    width: '100%',
    maxWidth: 350,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[5],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  modalSubTitle: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
    marginBottom: spacing[4],
  },
  modalDisplayBox: {
    borderBottomWidth: 2,
    borderBottomColor: '#059669',
    paddingBottom: spacing[1],
    marginBottom: 4,
    width: '100%',
  },
  modalDisplayText: {
    fontSize: 32,
    fontWeight: '300',
    color: '#0f172a',
    textAlign: 'right',
    width: '100%',
  },
  modalRangeText: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: spacing[5],
  },
  keypadGrid: {
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  keypadRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  keypadBtn: {
    flex: 1,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  keypadNumText: {
    fontSize: 22,
    fontWeight: '400',
    color: '#0f172a',
  },
  keypadActionText: {
    fontSize: 20,
    fontWeight: fontWeights.bold,
    color: '#334155',
  },
  quickZeroRow: {
    flexDirection: 'row',
    gap: spacing[2],
    marginBottom: spacing[4],
  },
  quickZeroBtn: {
    flex: 1,
    height: 36,
    backgroundColor: '#ecfdf5',
    borderWidth: 1,
    borderColor: '#a7f3d0',
    borderRadius: borderRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quickZeroBtnText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#047857',
  },
  modalActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
  },
  modalConfirmBtn: {
    backgroundColor: '#059669',
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.sm,
  },
  modalConfirmBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
  modalCancelBtn: {
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2.5],
  },
  modalCancelBtnText: {
    color: '#475569',
    fontSize: 14,
  },
});
